import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNull from 'lodash/isNull';
import isString from 'lodash/isString';
import { useMemo } from 'react';

import { useMatches } from 'shared/hooks/router';
import { useAppSelector } from 'shared/hooks/store';

import {
  handleArrayCrumb,
  handleStringCrumb,
  handleFunctionCrumb,
} from './utils';

import type { DynamicCrumb } from 'shared/store/breadcrumbs/types';

interface Crumb extends Pick<DynamicCrumb, 'isLoading'> {
  title: string | null;
  to: DynamicCrumb['pathname'];
}

export const useCrumbs = () => {
  const matches = useMatches();
  const { dynamicCrumb } = useAppSelector(state => state.breadcrumbs);

  return useMemo(
    () =>
      matches.reduce((acc: Crumb[], { handle, pathname }) => {
        const currentCrumb = dynamicCrumb[pathname];
        const currentHandle = handle?.crumb;

        if (isString(currentHandle)) {
          return handleStringCrumb(currentHandle, pathname, acc);
        }

        if (!currentCrumb || !has(dynamicCrumb, pathname)) return acc;

        const { title } = currentCrumb;

        if (isArray(title) && isFunction(currentHandle)) {
          return handleArrayCrumb(
            title,
            currentHandle,
            pathname,
            acc,
            dynamicCrumb,
          );
        }

        if (isFunction(currentHandle) && (isString(title) || isNull(title))) {
          return handleFunctionCrumb(
            title,
            currentHandle,
            pathname,
            acc,
            dynamicCrumb,
          );
        }

        return acc;
      }, []),
    [dynamicCrumb, matches],
  );
};
